import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { AnchoredSelection } from '../../../shared/annotations';
import type { AnnotationAction } from '../../../shared/types';
import { actionLabels, DecisionIcon, reviewQuietButton } from './ReviewUi';

/** Where the caret should point, in document coordinates. */
export interface ToolbarAnchor {
  x: number;
  /** Bottom edge of the selection, which the popover normally hangs below. */
  y: number;
  /** Top edge of the selection, which it points down at when it flips above. */
  top: number;
}

/** A drag anchors to a live browser selection; a paragraph mark has none. */
export type ToolbarSource = 'text' | 'paragraph';

interface AnnotationToolbarProps {
  selection: AnchoredSelection;
  quote: string;
  anchor: ToolbarAnchor | null;
  source: ToolbarSource;
  busy: boolean;
  error: string | null;
  onSubmit: (action: AnnotationAction, comment: string | null) => Promise<void>;
  onClose: () => void;
}

const TOOLBAR_MARGIN = 12;
/** Clearance between the selection and the caret tip. */
const TOOLBAR_GAP = 10;
/** Mockup: the caret sits 42px in from the popover's left edge. */
const CARET_INSET = 42;
const CARET_MIN = 16;
const QUOTE_LIMIT = 120;

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export interface ToolbarPlacement {
  left: number;
  top: number;
  caretLeft: number;
  /** Flipped over the selection because it would not fit underneath. */
  above: boolean;
}

/**
 * Places the popover against the selection in the offset parent's coordinate
 * space. Anchor and origin are both document coordinates, so the popover stays
 * with the text as the page scrolls — no scroll listener, no frozen viewport
 * coordinates to drift.
 *
 * It hangs below by default and flips above when the bottom of the viewport
 * would cut it off, which is the only place a reviewer cannot scroll it back
 * into view: below the last paragraph there is nothing left to scroll to.
 */
export function anchoredToolbarPlacement(
  anchor: ToolbarAnchor,
  origin: { left: number; top: number },
  toolbar: { width: number; height: number },
  viewport: { width: number; height: number; scrollY: number },
  margin = TOOLBAR_MARGIN,
): ToolbarPlacement {
  const maximumLeft = Math.max(margin, viewport.width - margin - toolbar.width);
  const documentLeft = Math.min(Math.max(anchor.x - CARET_INSET, margin), maximumLeft);
  const caretLimit = Math.max(CARET_MIN, toolbar.width - CARET_MIN);
  const bottomOverflows = anchor.y - viewport.scrollY + TOOLBAR_GAP + toolbar.height + margin > viewport.height;
  const roomAbove = anchor.top - viewport.scrollY - TOOLBAR_GAP - toolbar.height >= margin;
  const above = bottomOverflows && roomAbove;
  return {
    left: documentLeft - origin.left,
    top: (above ? anchor.top - TOOLBAR_GAP - toolbar.height : anchor.y + TOOLBAR_GAP) - origin.top,
    caretLeft: Math.min(Math.max(anchor.x - documentLeft, CARET_MIN), caretLimit),
    above,
  };
}

/** Bottom sheet, anchored popover, or measured-but-not-yet-placed. */
export type ToolbarMode = 'pending' | 'sheet' | 'anchored';

/**
 * Where the popover is allowed to sit. It opens pending — unmeasured — and
 * stays hidden and unfocused there, because an anchored popover with no
 * placement falls back to the top of the document: focusing it scrolls the page
 * away from the paragraph the reviewer just marked.
 *
 * Below md it is a bottom sheet, which needs no measurement. A desktop
 * selection the browser could not hand back a rectangle for — a zero-size range
 * right after mouseup — falls back to that same sheet rather than the top of
 * the page, so it is always visible and never moves the page under the pointer.
 */
export function toolbarModeFor(input: {
  desktop: boolean;
  hasAnchor: boolean;
  placed: boolean;
}): ToolbarMode {
  if (!input.desktop || !input.hasAnchor) return 'sheet';
  return input.placed ? 'anchored' : 'pending';
}

/** A pending popover has no resolved position, so it neither paints nor takes focus. */
export function toolbarIsPresentable(mode: ToolbarMode): boolean {
  return mode !== 'pending';
}

/**
 * A cleared selection closes the toolbar, but focusing the comment field
 * collapses the document selection in some browsers — so anything happening
 * inside the toolbar is never read as a dismissal, and a paragraph mark (which
 * never had a browser selection) is never dismissed this way at all.
 */
export function shouldCloseOnSelectionChange(input: {
  source: ToolbarSource;
  collapsed: boolean;
  focusInsideToolbar: boolean;
  selectionInsideToolbar: boolean;
}): boolean {
  if (input.source === 'paragraph') return false;
  if (input.focusInsideToolbar || input.selectionInsideToolbar) return false;
  return input.collapsed;
}

/**
 * A dialog stacked over the toolbar — Change your name, Submit review — owns
 * its own clicks and its own Escape. Tearing the toolbar down underneath it
 * loses the selection the reviewer opened the dialog to save.
 */
export function shouldCloseForOutsideEvent(input: {
  insideToolbar: boolean;
  dialogInvolved: boolean;
  defaultPrevented: boolean;
}): boolean {
  return !input.insideToolbar && !input.dialogInvolved && !input.defaultPrevented;
}

const DIALOG_SELECTOR = '[role="dialog"], dialog[open]';

/** Keeps the live quote to a readable line, cut at a word where it can be. */
export function truncateQuote(quote: string, limit = QUOTE_LIMIT): string {
  const tidy = quote.replace(/\s+/g, ' ').trim();
  if (tidy.length <= limit) return tidy;
  const cut = tidy.slice(0, limit);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export default function AnnotationToolbar({ selection, quote, anchor, source, busy, error, onSubmit, onClose }: AnnotationToolbarProps) {
  const toolbarRef = useRef<HTMLElement>(null);
  const firstButton = useRef<HTMLButtonElement>(null);
  const close = useRef(onClose);
  const [comment, setComment] = useState('');
  const [busyAction, setBusyAction] = useState<AnnotationAction | null>(null);
  const [layout, setLayout] = useState<{ mode: ToolbarMode; placement: ToolbarPlacement | null }>({ mode: 'pending', placement: null });
  const selectionKey = `${selection.start_segment_id}:${selection.start_offset}:${selection.end_segment_id}:${selection.end_offset}`;
  const presentable = toolbarIsPresentable(layout.mode);

  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  // Focus moves in once per selection, not on every parent render — and only
  // once the popover has a place to be. preventScroll keeps the page still even
  // if it somehow takes focus before then.
  useEffect(() => {
    if (!presentable) return;
    firstButton.current?.focus({ preventScroll: true });
  }, [presentable, selectionKey]);

  useEffect(() => {
    function keydown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      if (shouldCloseForOutsideEvent({
        insideToolbar: false,
        dialogInvolved: document.querySelector(DIALOG_SELECTOR) !== null,
        defaultPrevented: event.defaultPrevented,
      })) close.current();
    }
    function pointerdown(event: PointerEvent) {
      const toolbar = toolbarRef.current;
      if (!toolbar) return;
      const target = event.target instanceof Element
        ? event.target
        : event.target instanceof Node ? event.target.parentElement : null;
      if (shouldCloseForOutsideEvent({
        insideToolbar: event.target instanceof Node && toolbar.contains(event.target),
        dialogInvolved: Boolean(target?.closest(DIALOG_SELECTOR)),
        defaultPrevented: event.defaultPrevented,
      })) close.current();
    }
    document.addEventListener('keydown', keydown);
    document.addEventListener('pointerdown', pointerdown);
    return () => {
      document.removeEventListener('keydown', keydown);
      document.removeEventListener('pointerdown', pointerdown);
    };
  }, []);

  useEffect(() => {
    function selectionchange() {
      // Read once the browser has settled focus, or clicking into the comment
      // field reads as the reviewer clearing their selection.
      window.setTimeout(() => {
        const toolbar = toolbarRef.current;
        if (!toolbar) return;
        const current = window.getSelection();
        const dismissed = shouldCloseOnSelectionChange({
          source,
          collapsed: !current || current.isCollapsed || current.toString().trim().length === 0,
          focusInsideToolbar: toolbar.contains(document.activeElement),
          selectionInsideToolbar: Boolean(current?.anchorNode && toolbar.contains(current.anchorNode)),
        });
        if (dismissed) close.current();
      }, 0);
    }
    document.addEventListener('selectionchange', selectionchange);
    return () => document.removeEventListener('selectionchange', selectionchange);
  }, [source]);

  useBrowserLayoutEffect(() => {
    function position() {
      const toolbar = toolbarRef.current;
      if (!toolbar) return;
      const desktop = window.matchMedia('(min-width: 768px)').matches;
      if (!desktop || !anchor) {
        setLayout({ mode: toolbarModeFor({ desktop, hasAnchor: Boolean(anchor), placed: false }), placement: null });
        return;
      }
      const parent = toolbar.offsetParent as HTMLElement | null;
      const parentRect = parent?.getBoundingClientRect();
      const origin = parentRect
        ? { left: parentRect.left + window.scrollX, top: parentRect.top + window.scrollY }
        : { left: 0, top: 0 };
      const rect = toolbar.getBoundingClientRect();
      setLayout({
        mode: toolbarModeFor({ desktop, hasAnchor: true, placed: true }),
        placement: anchoredToolbarPlacement(
          anchor,
          origin,
          { width: rect.width, height: rect.height },
          {
            width: document.documentElement.clientWidth,
            height: document.documentElement.clientHeight,
            scrollY: window.scrollY,
          },
        ),
      });
    }
    position();
    window.addEventListener('resize', position);
    return () => window.removeEventListener('resize', position);
  }, [anchor?.x, anchor?.y, anchor?.top, error, quote]);

  async function choose(action: AnnotationAction) {
    setBusyAction(action);
    try {
      await onSubmit(action, comment.trim() || null);
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <aside
      ref={toolbarRef}
      /* z-50 clears the sticky review header, which sits at z-40. */
      className={`anno-pop z-50 rounded-card border border-line bg-card p-4 shadow-floating${layout.mode === 'sheet' ? '' : ' anno-pop-anchored'}${presentable ? '' : ' anno-pop-pending'}`}
      style={layout.placement ? { '--anno-left': `${layout.placement.left}px`, '--anno-top': `${layout.placement.top}px`, '--anno-caret-left': `${layout.placement.caretLeft}px` } as React.CSSProperties : undefined}
      data-placement={layout.placement?.above ? 'above' : undefined}
      aria-label="Annotation actions"
    >
      <span className="anno-caret" aria-hidden="true" />
      <div className="flex items-baseline justify-between gap-4">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">Your selection</p>
        <button type="button" className="-my-2 min-h-11 min-w-11 rounded-full text-xl font-bold text-faint hover:bg-paper-200 hover:text-fg" onClick={onClose} aria-label="Close annotation actions">×</button>
      </div>
      <p className="mt-1 text-[13px] italic text-muted">“{truncateQuote(quote)}”</p>
      <div className="mt-3.5 grid grid-cols-3 gap-1.5">
        {(['use', 'maybe', 'remove'] as const).map((action, index) => (
          <button key={action} ref={index === 0 ? firstButton : undefined} type="button" className={`app-button anno-action anno-action-${action}`} data-chosen={busyAction === action ? 'true' : undefined} onClick={() => void choose(action)} disabled={busy || busyAction !== null} aria-busy={busyAction === action || undefined}>
            {busyAction === action ? <span className="inline-spinner" aria-hidden="true" /> : <DecisionIcon action={action} className="h-3.5 w-3.5" />}{actionLabels[action]}
          </button>
        ))}
      </div>
      {/* The field and its Save button share one focus scope: focus moving
          between them must not collapse the field out from under the click. */}
      <div className="anno-comment mt-3.5 border-t border-line-soft pt-3.5" data-filled={comment.trim() ? 'true' : undefined}>
        <label className="sr-only" htmlFor="annotation-comment">Comment about this line</label>
        <textarea id="annotation-comment" className="anno-field" rows={1} value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Add a comment about this line — it shows up in Comments too…" />
        <div className="mt-2 flex justify-end">
          <button type="button" className={reviewQuietButton} disabled={busy || busyAction !== null || !comment.trim()} onMouseDown={(event) => event.preventDefault()} onClick={() => void choose('comment')} aria-busy={busyAction === 'comment' || undefined}>{busyAction === 'comment' ? <><span className="inline-spinner" aria-hidden="true" />Saving comment</> : 'Save comment'}</button>
        </div>
      </div>
      {error ? <p className="mt-3 rounded-input bg-error-tint p-3 text-sm text-error" role="alert">{error}</p> : null}
    </aside>
  );
}
